/**
 * Detect a new deployed Vite entry bundle without reloading the current page.
 * The server serves index.html with revalidation and JS under immutable,
 * content-hashed paths. Check index.html, not a second mutable build marker.
 */
export function entryAsset(documentToRead: Document, origin: string): string | null {
  const src = documentToRead.querySelector<HTMLScriptElement>('script[type="module"][src]')?.getAttribute('src');
  if (!src) return null;
  try {
    const url = new URL(src, origin);
    return url.origin === origin && /^\/assets\/index-[A-Za-z0-9_-]+\.js$/.test(url.pathname)
      ? url.pathname : null;
  } catch {
    return null;
  }
}

export async function deployedVersionChanged(
  current: string,
  fetcher: typeof fetch,
  origin: string,
  signal?: AbortSignal,
): Promise<boolean> {
  if (!/^\/assets\/index-[A-Za-z0-9_-]+\.js$/.test(current)) return false;
  const url = new URL('/', origin);
  url.searchParams.set('app_version_check', String(Date.now()));
  const response = await fetcher(url.toString(), {
    cache: 'no-store', credentials: 'same-origin', signal,
  });
  if (!response.ok || !response.headers.get('content-type')?.includes('text/html')) return false;
  const html = await response.text();
  if (html.length > 32_768) return false;
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const next = entryAsset(parsed, origin);
  return next !== null && next !== current;
}
